// Barra de transporte do edvid: play, mudo, timecode MONOESPAÇADO (sem isso cada
// dígito que muda empurra a linha inteira), botão IN/OUT de marcação, zoom e fit.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { fmt } from './edvidTime';
import type { MediaController, Playhead } from './playhead';
import { IconFit, IconFlag, IconMute, IconPause, IconPlay, IconVol } from './icons';

export function Transport({ controller, playhead, total, markArmed, onMark, slider, onSlider, onFit, children }: {
  controller: MediaController | null;
  playhead: Playhead;
  total: number;
  markArmed: boolean;
  onMark: () => void;
  slider: number;
  onSlider: (v: number) => void;
  onFit: () => void;
  children?: ReactNode;
}) {
  const nowRef = useRef<HTMLSpanElement>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    const paint = (t: number) => {
      if (nowRef.current) nowRef.current.textContent = fmt(t);
      const p = playhead.playing;
      setPlaying(prev => (prev === p ? prev : p));
    };
    paint(playhead.get());
    return playhead.subscribe(paint);
  }, [playhead]);

  // o estado de play também muda com a agulha parada (pausa no fim)
  useEffect(() => {
    const id = window.setInterval(() => {
      if (!controller) return;
      const p = !controller.isPaused();
      setPlaying(prev => (prev === p ? prev : p));
    }, 250);
    return () => window.clearInterval(id);
  }, [controller]);

  const btn = 'flex h-8 w-8 items-center justify-center rounded-xl border border-white/10 text-slate-200 transition-colors hover:bg-white/10 disabled:opacity-40';
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-white/10 bg-slate-950/70 px-3 py-2">
      <button
        type="button"
        className={`${btn} bg-indigo-500 text-white hover:bg-indigo-400 border-indigo-400/40`}
        disabled={!controller}
        onClick={() => { if (!controller) return; if (controller.isPaused()) controller.play(); else controller.pause(); }}
        title="Play / pause (espaço)"
        aria-label={playing ? 'Pausar' : 'Reproduzir'}
      >
        {playing ? <IconPause /> : <IconPlay />}
      </button>
      <button
        type="button"
        className={btn}
        disabled={!controller}
        onClick={() => { if (!controller) return; controller.setMuted(!controller.isMuted()); setMuted(controller.isMuted()); }}
        title="Mudo"
        aria-label="Mudo"
      >
        {muted ? <IconMute /> : <IconVol />}
      </button>
      <span className="font-mono text-xs font-semibold tabular-nums text-slate-100" ref={nowRef}>0:00.00</span>
      <span className="font-mono text-xs text-slate-600">/</span>
      <span className="font-mono text-xs tabular-nums text-slate-500">{fmt(total)}</span>
      <button
        type="button"
        onClick={onMark}
        className={`ml-1 flex h-8 items-center gap-1.5 rounded-xl border px-2.5 text-[11px] font-black uppercase tracking-wider transition-colors ${markArmed ? 'border-rose-400/60 bg-rose-500/20 text-rose-200' : 'border-white/10 text-slate-300 hover:bg-white/10'}`}
        title="Marcar IN / OUT (tecla M)"
      >
        <IconFlag width={12} height={12} />
        {markArmed ? 'OUT' : 'IN'}
      </button>
      {children}
      <div className="ml-auto flex items-center gap-2">
        <span className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-500">zoom</span>
        <input
          type="range" min={0} max={100} value={slider}
          onChange={e => onSlider(Number(e.target.value))}
          className="w-28 accent-indigo-500"
          title="Zoom da timeline (centrado na agulha)"
          aria-label="Zoom da timeline"
        />
        <button type="button" onClick={onFit} className={btn} title="Ajustar à janela" aria-label="Ajustar à janela"><IconFit /></button>
      </div>
    </div>
  );
}
